import firebase from 'firebase/compat/app';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { auth, db } from '../firebase/config';
import DB_TABLE from '../firebase/db-config';

export interface IAdministrators {
  uid: string;
  username: string;
}

export interface IRiders {
  uid: string;
  username: string;
}

export interface IStadium {
  administrators: IAdministrators[];
  super_administrators?: IAdministrators[];
  enable_sales_count: boolean;
  seat_number: string;
  fileUrl: string;
  elepay_secret_key: string;
  elepay_public_key: string;
  id: string;
  isSeeAll: boolean;
  isStadiumPromotion: boolean;
  last_update_date: string;
  latest_id: number;
  name: string;
  promotion_restaurant_id: string;
  service_name: string;
  stadium_profile_pic: string;
  status: string;
  riders: IRiders[];
  enable_drink_quantity_control: boolean;
  enable_food_quantity_control: boolean;
  enable_terms: boolean;
  max_drink_quantity: number;
  max_food_quantity: number;
  enable_order_reminder: boolean;
  order_reminder_en: string;
  order_reminder_ja: string;
  language: string;
  cms_settings: { [key: string]: boolean };
  location_id: string;
  merchant_id: string;
}

interface AuthContextValue {
  user: firebase.User | null;
  loading: boolean;
  isOnline: boolean;
  stadiumId: string | null;
  stadiumData: IStadium | null;
  logoutUser: () => void;
  updateUser: (_user: firebase.User) => void;
  onToggleOnline: (e: boolean) => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  isOnline: true,
  stadiumId: null,
  stadiumData: null,
  logoutUser: () => {},
  updateUser: (user) => {},
  onToggleOnline: (e) => {},
});

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<firebase.User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const [stadiumId, setStadiumId] = useState(null);
  const [stadiumData, setStadiumData] = useState<IStadium | null>(null);

  const updateUser: (_user: firebase.User | null) => any = (_user) => {
    setUser(_user);
    setLoading(false);
  };

  const logoutUser = () => {
    auth.signOut();
    window.localStorage.removeItem('id_token');
    setUser(null);
  };

  const onToggleOnline = (e: boolean) => {
    if (user?.uid) {
      db.collection(DB_TABLE.DB_TABLE_USERS)
        .doc(user.uid)
        .update({ is_online: e })
        .then((res) => {
          setIsOnline(e);
        });
    }
  };

  useEffect(() => {
    setLoading(true);
    auth.onAuthStateChanged(updateUser);
  }, []);

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (user?.uid) {
        try {
          const result = await db.collection(DB_TABLE.DB_TABLE_USERS).doc(user.uid).get();
          const userInfo = result.data();
          if (userInfo) {
            setIsOnline(userInfo?.is_online ?? false);
            setStadiumId(userInfo.stadium_id || '');
            if (userInfo.stadium_id) {
              const stadiumDoc = await db.collection(DB_TABLE.DB_TABLE_STADIUM_MANAGE).doc(userInfo.stadium_id).get();
              if (stadiumDoc.exists) {
                setStadiumData(stadiumDoc.data() as IStadium);
              }
            }
          }
        } catch (err) {
          console.log(err);
        }
      }
    };

    fetchUserInfo();
  }, [user?.uid]);

  return (
    <AuthContext.Provider
      value={{
        user,
        stadiumId,
        stadiumData,
        loading,
        isOnline,
        logoutUser,
        updateUser,
        onToggleOnline,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuthContext = () => {
  const context = useContext(AuthContext);
  return context;
};

export { AuthProvider, useAuthContext };

